import React, {useCallback, useEffect, useState} from 'react';
import {
    Button,
    CheckBox,
    DropdownItem,
    Flex,
    HTMLDialog,
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    Text,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {DialogToolbarContent} from '../../../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {priority} from '@priority-software/priority-proto-api/lib';
import {HomepageItemData, searchTiles} from '../../../../api';
import {TileIcon, TileIconType} from '../../../../components/Tile/enum';
import {isRTL} from '../../../../../../Localization';
import {useScrollWrapDimensions} from '../../../../../../hooks/customScroll/useScrollWrapDimensions';
import {Loader} from '../../../../../PartModal/components/Loader/Loader';
import {ReactComponent as EmptySearchIcon} from './empty-search-icon.svg';
import {showFormClientSideMessage} from '../../../../../Form/actions/messages';
import {isEnterKey} from '../../../../../../utils/isEnterKey';
import {getHelpData} from './utils';
import {getGroupTitle} from '../../../../utils';

import texts from '../../../../texts';
import styles from './styles.module.scss';

type Props = {
    onClose: () => void;
    onSave: (exec: string, title: string, groupName: string) => void;
    groupName?: string;
};

export const AddTileDialog = (props: Props) => {
    const {onClose, groupName} = props;
    const inputRef = React.createRef<HTMLInputElement>();
    const [searchValue, setSearchValue] = React.useState('');
    const [ignoreCase, setIgnorecase] = React.useState(true);
    const [options, setOptions] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const {maxHeight, setWrapRef} = useScrollWrapDimensions();
    const {focusedIndex, setFocusedIndex, handleNavigationKeys} = useKeyboardNavigation({
        isOpen: true,
        optionsCount: options?.length || 0
    });

    const focusedOption = options?.[focusedIndex];

    useEffect(() => {
        inputRef.current?.focus();
    }, [inputRef]);

    useEffect(() => {
        window.addEventListener('keydown', handleNavigationKeys);
        return () => window.removeEventListener('keydown', handleNavigationKeys);
    }, [handleNavigationKeys, options]);

    const itemRenderer = (item: {
        label: string;
        value: string | number | boolean;
        data?: unknown;
    }) => {
        const data = item.data as HomepageItemData;
        const type = data.type;
        const isProcIcon =
            type === TileIconType.P || type === TileIconType.G || type === TileIconType.E;

        return (
            <Flex
                justifyContent="flex-start"
                alignItems="center"
                componentClasses={styles.dropdownItemContent}
            >
                <div
                    className={cx(styles.itemIcon, {
                        [styles.formIcon]: type === TileIconType.F,
                        [styles.procIcon]: isProcIcon,
                        [styles.repIcon]: type === TileIconType.R,
                        [styles.menuIcon]: type === TileIconType.H,
                        [styles.biIcon]: type === TileIconType.O,
                        [styles.wizardIcon]: type === TileIconType.B
                    })}
                >
                    <Icon icon={TileIcon[type]}></Icon>
                </div>
                <Text skin="paragraph-2">{item.label}</Text>
            </Flex>
        );
    };

    const onSearch = async () => {
        setIsProcessing(true);
        const res = await searchTiles(searchValue, ignoreCase);
        setIsProcessing(false);
        setOptions(res.map(item => ({value: item.exec, label: item.title, data: item})));
        setFocusedIndex(0);
    };

    const showHelp = () => {
        if (focusedOption) {
            const {helpText, linkData} = getHelpData(focusedOption.data);
            const space = '\u00a0'.repeat(2);

            if (helpText) {
                showFormClientSideMessage('', priority.netitems.shared.Message.Type.Information, {
                    title: `${texts.priority_help_label}:${space}${focusedOption.label}`,
                    content: helpText,
                    confirmButtonText: texts.group_name_exists_dialog_confirm,
                    linkData
                });
            }
        }
    };

    const onSelect = useCallback(
        (option, isSelected: boolean) => {
            if (isSelected) {
                setSelectedOption(option);
                setFocusedIndex(options?.indexOf(option) ?? -1);
            }
        },
        [options]
    );

    const addTile = () => {
        if (selectedOption) {
            props.onSave(selectedOption.value, selectedOption.label, groupName);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isEnterKey(e) && !!searchValue) {
            onSearch();
        }
    };

    const clearSearch = () => {
        setSearchValue('');
        setOptions(null);
        setSelectedOption(null);
    };

    const renderContent = () => {
        if (isProcessing) {
            return <Loader />;
        }
        if (!options) {
            return (
                <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={{paddingTop: 24}}
                >
                    <EmptySearchIcon width={180} height={152} />
                    <Text skin="paragraph-2" style={{textAlign: 'center'}}>
                        {texts.search_tiles_list_empty}
                    </Text>
                </Flex>
            );
        } else if (options.length === 0) {
            return (
                <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="flex-start"
                    style={{paddingTop: 24}}
                >
                    <EmptySearchIcon width={180} height={152} />
                    <Text skin="paragraph-2" style={{textAlign: 'center'}}>
                        {texts.search_tiles_empty_search.replace('X', searchValue)}
                    </Text>
                </Flex>
            );
        }

        return (
            <Scrollbar
                autoHeight
                autoHeightMax={maxHeight}
                rtl={isRTL()}
                shiftVerticalScrollPositionX={12}
            >
                {options.map((item, index) => (
                    <DropdownItem
                        key={index}
                        data={item}
                        onSelect={onSelect}
                        isFocused={index === focusedIndex}
                        isSelected={selectedOption === item}
                        itemBorder
                        contentRenderer={itemRenderer}
                    />
                ))}
            </Scrollbar>
        );
    };

    return (
        <HTMLDialog
            isOpen
            width={720}
            onClose={onClose}
            componentClasses={styles.component}
            toolbarContent={
                <DialogToolbarContent
                    mainTitle={texts.add_tile_main_title}
                    subTitle={getGroupTitle(groupName)}
                    subTitleColor="primary-500"
                />
            }
            title={
                <Flex direction="column" alignItems="flex-start" componentClasses={styles.header}>
                    <div style={{display: 'flex', width: '100%', gap: 16, padding: '2px 0'}}>
                        <Input
                            inputRef={inputRef}
                            size="large"
                            onChange={e => setSearchValue(e.target.value)}
                            value={searchValue}
                            onKeyDown={handleKeyDown}
                            placeholder={texts.search_tiles_input_placeholder}
                        >
                            {!!searchValue && (
                                <Icon
                                    iconColor="secondary-200"
                                    icon={PriorityIcon.closeSmall}
                                    onClick={clearSearch}
                                />
                            )}
                        </Input>
                        <Button
                            skin="primary"
                            size="medium"
                            onClick={onSearch}
                            disabled={!searchValue}
                        >
                            {texts.search_tiles_btn}
                        </Button>
                    </div>

                    <div style={{marginBottom: 8}}>
                        <CheckBox
                            checked={ignoreCase}
                            onChange={() => setIgnorecase(state => !state)}
                        >
                            {texts.ignore_case_label}
                        </CheckBox>
                    </div>
                </Flex>
            }
            content={
                <div className="pui-scrollable-wrap" ref={setWrapRef}>
                    {renderContent()}
                </div>
            }
            footer={
                <Flex>
                    <Button
                        skin="text"
                        size="large"
                        onClick={showHelp}
                        disabled={!Boolean(focusedOption) || isProcessing}
                    >
                        {texts.help_label}
                    </Button>

                    <Flex justifyContent="flex-end" style={{gap: 24}}>
                        <Button skin="secondary" onClick={onClose} size="large" wideButton>
                            {texts.btn_cancel}
                        </Button>

                        <Button
                            skin="primary"
                            onClick={addTile}
                            size="large"
                            wideButton
                            disabled={!Boolean(selectedOption)}
                        >
                            {texts.add_tile_btn}
                        </Button>
                    </Flex>
                </Flex>
            }
        />
    );
};
